import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Text } from '@uhg-abyss/web/ui/Text';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../../../../common/AdobeTagging/adobeModalTrackEvent';
import { Constants } from '../../../../../common/Constants';
import { getProviderTitle } from '../../../../../common/Utils';
import { useCurrentMemberIsPreEffective } from '../../../../../hooks/useCurrentMemberIsPreEffective/useCurrentMemberIsPreEffective';
import { useLagoon } from '../../../../../hooks/useLagoon';
import { useChipStore } from '../../../../../store/useChipStore';
import { ChipState } from '../../../../../store/useChipStore/chipStore';
import { useTypeaheadStore } from '../../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../../store/useTypeaheadStore/typeaheadStore';
import {
  LogoNotAvailableContainer,
  virtualCareProviderCardCss,
  virtualCareProviderCardLinkCss,
  virtualCareProviderCardText,
  virtualCareSpecialityCardCss,
} from './VirtualCareProvidersBanner.styled';

interface Provider {
  providerName: string;
  logo?: string;
  link: string;
  linkType: string;
}

interface VirtualCareProviderCardProps {
  provider: Provider;
  serviceType: string;
  mobileScreen: boolean;
  mapView: boolean;
  setSelectedProviderName: (name: string) => void;
  setIsModalOpen: (open: boolean) => void;
  primaryCareVirtual: boolean;
}

export const VirtualCareProviderCard: React.FC<
  VirtualCareProviderCardProps
> = ({
  provider,
  serviceType,
  mobileScreen,
  mapView,
  setSelectedProviderName,
  setIsModalOpen,
  primaryCareVirtual = false,
}) => {
  const { t } = useTranslation();
  const isPreEffective = useCurrentMemberIsPreEffective();
  const { fromViewAll = false } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      fromViewAll: state.typeaheadSearchStore.fromViewAll,
    }))
  );
  const { getRouteParams } = useRouter();
  const urlParams = getRouteParams();

  const uiMessaging = useLagoon('ui-messaging')();
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const chipValueFromUrl = urlParams?.chipValue || chipStore.chipValue;
  const virtualCareType =
    chipValueFromUrl === Constants.CHIPS_CATEGORIES.PRIMARY_CARE
      ? 'Virtual Primary Care'
      : 'Virtual Specialty Care';

  const handleGetCareClick = (e) => {
    e.preventDefault();
    if (isPreEffective) return;
    setSelectedProviderName(provider?.providerName);
    setIsModalOpen(true);
    if (primaryCareVirtual) {
      adobeLinkTrackEvent({
        name: t('VIRTUAL_CARE_PROVIDERS_BANNER.GET_CARE'),
        location: `${virtualCareType} ${provider?.providerName}`,
        type: 'external',
      });
      adobeModalTrackEvent('continue to site');
    }
  };
  return (
    <Card
      css={virtualCareProviderCardCss(
        mobileScreen || mapView,
        primaryCareVirtual
      )}
      data-testid="virtual-care-provider-card"
      key={provider?.providerName}
    >
      <Flex justify="space-between">
        <Flex alignItems="flex-start" direction="column" justify="center">
          {provider?.logo ? (
            <img
              alt={`${provider?.providerName} logo`}
              data-testid={`virtual-care-provider-logo`}
              src={provider?.logo}
              style={{
                height:
                  chipValueFromUrl === Constants.CHIPS_CATEGORIES.PRIMARY_CARE
                    ? '30px'
                    : '20px',
                maxHeight:
                  chipValueFromUrl === Constants.CHIPS_CATEGORIES.PRIMARY_CARE
                    ? '30px'
                    : '20px',
                maxWidth: mobileScreen || mapView ? '120px' : '165px',
                marginBottom: 0,
                objectFit: 'contain' as const,
                display: 'block',
                overflow: 'hidden',
              }}
            />
          ) : (
            <LogoNotAvailableContainer
              css={{
                ...virtualCareSpecialityCardCss(
                  fromViewAll || mapView,
                  mobileScreen,
                  primaryCareVirtual
                ),
                height:
                  chipValueFromUrl === Constants.CHIPS_CATEGORIES.PRIMARY_CARE
                    ? '30px'
                    : '20px',
                overflow: 'hidden',
              }}
              data-testid={'logo-not-available'}
            >
              {t('Logo not available')}
            </LogoNotAvailableContainer>
          )}
          <Text
            css={virtualCareProviderCardText(mobileScreen || mapView)}
            data-testid="virtual-care-provider-service-type"
          >
            {getProviderTitle(serviceType, uiMessaging, provider)}
          </Text>
        </Flex>
        {!isPreEffective && (
          <Flex alignItems="center" justify="center">
            <Link
              afterIcon="open_in_new"
              css={virtualCareProviderCardLinkCss(mobileScreen || mapView)}
              data-auto-testid={`virtual-care-provider-link`}
              data-testid={`virtual-care-provider-link`}
              onClick={handleGetCareClick}
            >
              {t('VIRTUAL_CARE_PROVIDERS_BANNER.GET_CARE')}
            </Link>
          </Flex>
        )}
      </Flex>
    </Card>
  );
};
